import {
  Component,
  OnInit,
  ViewChild,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserModel } from '../../../../core/models/data/user.model';
import { PaymentMethodType } from '../../../../core/enums/data.enums';
import { PaymentMethodModel } from '../../../../core/models/service/payment-method.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CARD_BRAND_DISPLAY_LABELS, CARD_BRAND_ICONS } from '../../../../core/constants/display-labels.constants';
import { UserService } from '../../../../core/services/user.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { StripeMessagingConstants } from '../../../../core/constants/messaging.constants';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { StripeAddCreditCardComponent } from '../../../stripe/components/stripe-add-credit-card/stripe-add-credit-card.component';
import { StripeAddBankAccountComponent } from '../../../stripe/components/stripe-add-bank-account/stripe-add-bank-account.component';

@Component({
  selector: 'ms-user-payment-methods',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, ConfirmationModalComponent, InfoModalComponent, StripeAddCreditCardComponent, StripeAddBankAccountComponent],
  templateUrl: './user-payment-methods.component.html',
  styleUrl: './user-payment-methods.component.scss'
})
export class UserPaymentMethodsComponent implements OnInit {
  @ViewChild('deleteConfirmationModal', { read: ConfirmationModalComponent }) deleteConfirmationModal?: ConfirmationModalComponent;
  @ViewChild('paymentInfoModal', { read: InfoModalComponent }) paymentInfoModal?: InfoModalComponent;
  @ViewChild('addCreditCardComponent') addCreditCardComponent?: StripeAddCreditCardComponent;

  private userService = inject(UserService);
  private userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);

  public user = input.required<UserModel>();
  public isOwnProfile = computed(() => this.userSessionService.isCurrentUser(this.user().userId));

  public paymentMethods = signal<PaymentMethodModel[]>([]);
  public isLoading = signal(false);
  public errorMessage = signal<string | null>(null);
  public showAddForm = signal(false);
  public selectedPaymentMethodType = signal<PaymentMethodType | null>(null);
  public PaymentMethodType = PaymentMethodType;
  public StripeMessagingConstants = StripeMessagingConstants;
  public isDeleting = signal(false);

  private paymentMethodToDelete: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadPaymentMethods();
  }

  public async loadPaymentMethods(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const result = await this.userService.getPaymentMethods(this.user().userId);

      if (result.success && result.data) {
        this.paymentMethods.set(result.data.paymentMethods);
      } else {
        this.errorMessage.set(result.message || StripeMessagingConstants.STRIPE_ERROR_LOADING_PAYMENT_METHODS());
      }
    } catch {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_LOADING_PAYMENT_METHODS());
    } finally {
      this.isLoading.set(false);
    }
  }

  public showPaymentMethodTypeSelector(): void {
    this.showAddForm.set(true);
    this.selectedPaymentMethodType.set(null);
  }

  public selectPaymentMethodType(type: PaymentMethodType): void {
    this.selectedPaymentMethodType.set(type);

    if (type === PaymentMethodType.Card) {
      setTimeout(async () => {
        if (this.addCreditCardComponent) {
          await this.addCreditCardComponent.initialize();
        }
      }, 0);
    }
  }

  public async onPaymentMethodSaved(): Promise<void> {
    const isBankAccount = this.selectedPaymentMethodType() === PaymentMethodType.AchDebit;
    this.showAddForm.set(false);
    this.selectedPaymentMethodType.set(null);

    const message = isBankAccount
      ? StripeMessagingConstants.STRIPE_BANK_ACCOUNT_ADDED()
      : StripeMessagingConstants.STRIPE_PAYMENT_METHOD_ADDED();

    this.paymentInfoModal?.message.set(message);
    this.paymentInfoModal?.modalType.set(ModalTypes.Success);
    this.bootstrapService.manageModal('#paymentInfoModal', ModalActions.Show);
    
    await this.loadPaymentMethods();
  }

  public onAddFormCancelled(): void {
    this.showAddForm.set(false);
    this.selectedPaymentMethodType.set(null);
  }

  public openDeleteConfirmation(paymentMethodId: string): void {
    this.paymentMethodToDelete = paymentMethodId;
    this.bootstrapService.manageModal('#deletePaymentMethodModal', ModalActions.Show);
  }

  public async confirmDeletePaymentMethod(): Promise<void> {
    if (!this.paymentMethodToDelete) {
      return;
    }

    this.isDeleting.set(true);

    try {
      const result = await this.userService.deletePaymentMethod(this.user().userId, this.paymentMethodToDelete);

      this.bootstrapService.manageModal('#deletePaymentMethodModal', ModalActions.Hide);

      if (result.success) {
        this.paymentInfoModal?.message.set(StripeMessagingConstants.STRIPE_PAYMENT_METHOD_DELETED());
        this.paymentInfoModal?.modalType.set(ModalTypes.Success);
        await this.loadPaymentMethods();
      } else {
        this.paymentInfoModal?.message.set(result.message || StripeMessagingConstants.STRIPE_ERROR_DELETING_PAYMENT_METHOD());
        this.paymentInfoModal?.modalType.set(ModalTypes.Failure);
      }

      this.bootstrapService.manageModal('#paymentInfoModal', ModalActions.Show);
    } catch {
      this.bootstrapService.manageModal('#deletePaymentMethodModal', ModalActions.Hide);
      this.paymentInfoModal?.message.set(StripeMessagingConstants.STRIPE_ERROR_DELETING_PAYMENT_METHOD());
      this.paymentInfoModal?.modalType.set(ModalTypes.Failure);
      this.bootstrapService.manageModal('#paymentInfoModal', ModalActions.Show);
    } finally {
      this.isDeleting.set(false);
      this.paymentMethodToDelete = null;
    }
  }

  public cancelDelete(): void {
    this.bootstrapService.manageModal('#deletePaymentMethodModal', ModalActions.Hide);
    this.paymentMethodToDelete = null;
  }

  public closeInfoModal(): void {
    this.bootstrapService.manageModal('#paymentInfoModal', ModalActions.Hide);
  }

  public getCardDisplayName(method: PaymentMethodModel): string {
    if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
      const brandName = CARD_BRAND_DISPLAY_LABELS[method.card.brand] ?? PaymentMethodType.Card;
      return `${brandName} ending in ${method.card.last4}`;
    }
    if (method.paymentMethodType === PaymentMethodType.AchDebit && method.usBankAccount) {
      return `${method.usBankAccount.bankName} ending in ${method.usBankAccount.last4}`;
    }
    return 'Unknown payment method';
  }

  public getCardExpiry(method: PaymentMethodModel): string {
    if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
      return `${String(method.card.expMonth).padStart(2, '0')}/${method.card.expYear}`;
    }
    return '';
  }

  public getPaymentMethodIcon(method: PaymentMethodModel): IconProp {
    if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
      return CARD_BRAND_ICONS[method.card.brand] ?? ['fad', 'credit-card'];
    }
    if (method.paymentMethodType === PaymentMethodType.AchDebit) {
      return ['fad', 'building-columns'];
    }
    return ['fad', 'credit-card'];
  }
}
